import axios from "axios"
import { environment } from "../../environments/environment";
import { parseJwt } from "../utils/JWTParser";
import { logger, analytics } from "./analytics";
import { EmailNotValidatedError, AuthenticationError, NetworkError, SessionExpiredError, isNetworkError, isSessionExpiredError } from "../utils/errors";
import { AUTH_CONSTANTS } from "../utils/auth-constants";
import { clearAuthData, getRefreshToken, getAuthToken, setAuthToken, setRefreshToken } from "./token-manager";

interface LoginData {
    username: string,
    password: string
};

interface LoginResponse {
    access: string,
    refresh: string
};

export interface TokenParams {
    token_type: string,
    exp: number,
    iat: number,
    jti: string,
    user_id: number,
    legacy_password_login: boolean,
    em_unit_id?: number,
    candidate_id?: string,
    manager_id?: string,
    company_id?: string,
    profiles: ('candidate' | 'job_manager' | 'em_job_manager')[]
}

interface OTPData {
    email: string
};

interface OTPConfirmationData {
    email: string,
    code: string
};

interface ChangePasswordData {
    new_password1: string,
    new_password2: string
};

interface ResendVerificationEmailData {
    email: string
};

interface ChangeEmailRequestData {
    new_email: string
};

interface ChangeEmailValidateData {
    new_email: string,
    code: string
};

export const login = async (data: LoginData) => {
    // Breadcrumb: Início da tentativa de login
    analytics.addBreadcrumb('Login attempt started', 'auth', 'info');
    
    try {
        const response = await axios.post<LoginResponse>(environment.apiUrl+'/api/token/', data, {
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if(response.status < 200 || response.status >= 300) {
            analytics.addBreadcrumb('Login failed - invalid status', 'auth', 'error');
            throw new Error('Erro de autenticação: Status inválido');
        };

        analytics.addBreadcrumb('Login successful - tokens received', 'auth', 'info');
        return response;
    } catch (error: any) {
        // Adicionar contexto específico para erros de login
        if (error.response?.status === 401) {
            // Verificar se é erro de email não validado
            if (error.response?.data?.error === AUTH_CONSTANTS.ERRORS.EMAIL_NOT_VALIDATED) {
                analytics.addBreadcrumb('Login blocked - email not validated', 'auth', 'warning');
                
                throw new EmailNotValidatedError(
                    error.response.data.message,
                    {
                        instructions: error.response.data.instructions,
                        supportMessage: error.response.data.support_message,
                        canResendEmail: error.response.data.can_resend_email,
                        resendEndpoint: error.response.data.resend_endpoint,
                    }
                );
            }
            
            // Credenciais inválidas
            analytics.addBreadcrumb('Login failed - invalid credentials', 'auth', 'warning');

            // Adicionar contexto para Sentry (SEM dados pessoais)
            if (typeof window !== 'undefined' && (window as any).Sentry) {
                (window as any).Sentry.withScope((scope: any) => {
                    scope.setTag('error_type', AUTH_CONSTANTS.ERRORS.AUTHENTICATION_FAILED);
                    scope.setTag('http_status', '401');
                    scope.setContext('login_attempt', {
                        timestamp: new Date().toISOString(),
                        user_agent: navigator.userAgent,
                        url: window.location.href
                    });
                    scope.setContext('api_response', {
                        status: error.response?.status,
                        statusText: error.response?.statusText,
                    });
                });
            }

            throw new AuthenticationError();
        }
        
        // Outros erros HTTP
        if (error.response) {
            analytics.addBreadcrumb(
                `Login failed - HTTP ${error.response.status}`, 
                'auth', 
                'error'
            );
        } else if (error.request) {
            analytics.addBreadcrumb('Login failed - network error', 'auth', 'error');
        }
        
        // Re-throw o erro original para outros casos
        throw error;
    }
};

export const requestOTP = async (data: OTPData) => {
    analytics.addBreadcrumb('Requesting OTP', 'auth', 'info');
    
    try {
        const response = await axios.post<any>(
            environment.apiUrl + '/api/auth/request-code/', 
            data, 
            {
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        );

        if(response.status < 200 || response.status >= 300) {
            analytics.addBreadcrumb('Failed to request OTP - invalid status', 'auth', 'error');
            throw new Error('Erro ao solicitar código de verificação');
        }

        analytics.addBreadcrumb('OTP requested successfully', 'auth', 'info');
        return response;
    } catch (error: any) {
        // Verificar se é erro de email não validado
        const responseStatus = error.response?.status;
        const responseError = error.response?.data?.error;
        const isEmailNotValidated = responseStatus === 403 && 
            responseError === AUTH_CONSTANTS.ERRORS.EMAIL_NOT_VALIDATED;
        
        analytics.addBreadcrumb(
            `OTP error check - status: ${responseStatus}, error: ${responseError}, isEmailNotValidated: ${isEmailNotValidated}`, 
            'auth', 
            'info'
        );
        
        if (isEmailNotValidated) {
            analytics.addBreadcrumb('OTP request blocked - email not validated', 'auth', 'warning');
            
            const responseData = error.response.data || {};
            const errorMessage = responseData.message || 
                'Por favor, confirme seu email antes de recuperar sua senha. Verifique sua caixa de entrada.';
            
            throw new EmailNotValidatedError(
                errorMessage,
                {
                    instructions: responseData.instructions || [],
                    supportMessage: responseData.support_message,
                    canResendEmail: responseData.can_resend_email !== false,
                    resendEndpoint: responseData.resend_endpoint || '/api/resend-verification-email/',
                }
            );
        }
        
        analytics.addBreadcrumb('Error requesting OTP', 'auth', 'error');
        logger.error('Erro ao solicitar OTP', error);
        throw error;
    }
};

export const validateOTP = async (data: OTPConfirmationData) => {
    analytics.addBreadcrumb('Validating OTP', 'auth', 'info');
    
    try {
        const response = await axios.post<any>(
            environment.apiUrl + '/api/auth/validate-code/', 
            data, 
            {
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        );

        if(response.status < 200 || response.status >= 300) {
            analytics.addBreadcrumb('Failed to validate OTP - invalid status', 'auth', 'error');
            throw new Error('Erro ao validar código de verificação');
        }

        analytics.addBreadcrumb('OTP validated successfully', 'auth', 'info');
        return response;
    } catch (error: any) {
        analytics.addBreadcrumb('Error validating OTP', 'auth', 'error');
        logger.error('Erro ao validar OTP', error);
        throw error;
    }
};

export const changePassword = async (data: ChangePasswordData) => {
    analytics.addBreadcrumb('Changing password', 'auth', 'info');
    
    try {
        const response = await axios.post<any>(
            environment.apiUrl + '/api/auth/password/set/', 
            data, 
            {
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        );

        if(response.status < 200 || response.status >= 300) {
            analytics.addBreadcrumb('Failed to change password - invalid status', 'auth', 'error');
            throw new Error('Erro ao alterar senha');
        }

        analytics.addBreadcrumb('Password changed successfully', 'auth', 'info');
        return response;
    } catch (error: any) {
        analytics.addBreadcrumb('Error changing password', 'auth', 'error');
        logger.error('Erro ao alterar senha', error);
        throw error;
    }
};

export const resendVerificationEmail = async (data: ResendVerificationEmailData) => {
    analytics.addBreadcrumb('Resending verification email', 'auth', 'info');
    
    try {
        const response = await axios.post<any>(
            environment.apiUrl + '/api/resend-verification-email/', 
            data, 
            {
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        );

        if(response.status < 200 || response.status >= 300) {
            analytics.addBreadcrumb('Failed to resend verification email - invalid status', 'auth', 'error');
            throw new Error('Erro ao enviar email de verificação');
        }

        analytics.addBreadcrumb('Verification email resent successfully', 'auth', 'info');
        return response;
    } catch (error: any) {
        analytics.addBreadcrumb('Error resending verification email', 'auth', 'error');
        logger.error('Erro ao reenviar email de verificação', error);
        throw error;
    }
};

/**
 * Solicita código OTP para alteração de email
 */
export const requestEmailChangeCode = async (data: ChangeEmailRequestData) => {
    analytics.addBreadcrumb('Requesting email change code', 'auth', 'info');
    
    try {
        const token = getAuthToken();
        if (!token) {
            throw new Error('Token de autenticação não encontrado');
        }

        const response = await axios.post<any>(
            environment.apiUrl + '/api/auth/change-email/request-code/', 
            data, 
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            }
        );

        if(response.status < 200 || response.status >= 300) {
            analytics.addBreadcrumb('Failed to request email change code - invalid status', 'auth', 'error');
            throw new Error('Erro ao solicitar código de verificação');
        }

        analytics.addBreadcrumb('Email change code requested successfully', 'auth', 'info');
        return response;
    } catch (error: any) {
        analytics.addBreadcrumb('Error requesting email change code', 'auth', 'error');
        logger.error('Erro ao solicitar código de alteração de email', error);
        throw error;
    }
};

/**
 * Valida código OTP e altera o email
 */
export const validateEmailChangeCode = async (data: ChangeEmailValidateData) => {
    analytics.addBreadcrumb('Validating email change code', 'auth', 'info');
    
    try {
        const token = getAuthToken();
        if (!token) {
            throw new Error('Token de autenticação não encontrado');
        }

        const response = await axios.post<any>(
            environment.apiUrl + '/api/auth/change-email/validate-code/', 
            data, 
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            }
        );

        if(response.status < 200 || response.status >= 300) {
            analytics.addBreadcrumb('Failed to validate email change code - invalid status', 'auth', 'error');
            throw new Error('Erro ao validar código de verificação');
        }

        analytics.addBreadcrumb('Email change code validated successfully', 'auth', 'info');
        return response;
    } catch (error: any) {
        analytics.addBreadcrumb('Error validating email change code', 'auth', 'error');
        logger.error('Erro ao validar código de alteração de email', error);
        throw error;
    }
};

// Variável para evitar múltiplas tentativas de refresh simultâneas
let isRefreshing = false;
let refreshPromise: Promise<LoginResponse> | null = null;

// Função para renovar o access token usando refresh token
export const refreshAccessToken = async (): Promise<LoginResponse> => {
    // Se já está fazendo refresh, retornar a promise existente
    if (isRefreshing && refreshPromise) {
        logger.auth('Refresh já em andamento, aguardando...', {
            timestamp: new Date().toISOString()
        });
        return refreshPromise;
    }

    const refreshToken = getRefreshToken();
    
    logger.auth('Tentando renovar token', {
        hasRefreshToken: !!refreshToken,
        refreshTokenLength: refreshToken?.length || 0,
        isRefreshing
    });
    
    if (!refreshToken) {
        logger.auth('Refresh token não encontrado no localStorage', {
            reason: 'no_refresh_token'
        });
        
        // Limpar dados de autenticação
        clearAuthData('no_refresh_token');
        
        throw new SessionExpiredError('Sessão expirada. Faça login novamente.', 'no_refresh_token');
    }

    // Validar se o refresh token não expirou antes de tentar fazer a requisição
    try {
        const parsedRefreshToken = parseJwt(refreshToken);
        if (parsedRefreshToken && parsedRefreshToken.exp) {
            const now = Math.floor(Date.now() / 1000);
            const isExpired = parsedRefreshToken.exp < now;
            
            if (isExpired) {
                logger.auth('Refresh token já expirou, limpando tokens', {
                    reason: 'refresh_token_expired',
                    expiredAt: new Date(parsedRefreshToken.exp * 1000).toISOString(),
                    now: new Date(now * 1000).toISOString()
                });
                
                // Limpar dados de autenticação
                clearAuthData('refresh_token_expired');
                
                throw new SessionExpiredError('Sessão expirada. Faça login novamente.', 'refresh_token_expired');
            }
        }
    } catch (error: any) {
        // Se não conseguir parsear ou se expirou, limpar e lançar erro
        if (error instanceof SessionExpiredError) {
            throw error;
        }
        
        // Se erro ao parsear, continuar tentando o refresh (o backend validará)
        logger.auth('Não foi possível validar o refresh token localmente, tentando no backend', {
            error: error.message
        });
    }

    isRefreshing = true;
    refreshPromise = (async () => {
        try {
            logger.api('Enviando requisição de refresh', {
                url: environment.apiUrl+'/api/token/refresh/'
            });
            
            const response = await axios.post<LoginResponse>(environment.apiUrl+'/api/token/refresh/', {
                refresh: refreshToken
            }, {
                headers: {
                    'Content-Type': 'application/json'
                },
                timeout: 10000 // 10 segundos de timeout para evitar espera infinita
            });

            logger.auth('Token renovado com sucesso', {
                status: response.status,
                hasAccessToken: !!response.data?.access,
                hasRefreshToken: !!response.data?.refresh
            });

            if(response.status < 200 || response.status >= 300) {
                logger.error('Status inesperado na resposta de refresh', undefined, {
                    status: response.status
                });
                throw new Error('Erro ao renovar token: Status inválido');
            }

            return response.data;
        } catch (error: any) {
            logger.error('Erro ao renovar token', error, {
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                detail: error.response?.data?.detail
            });
            
            // Se o refresh token também expirou, limpar todos os tokens
            if (error.response?.status === 401) {
                logger.auth('Refresh token expirado ou inválido, limpando todos os tokens', {
                    reason: 'refresh_token_expired_or_invalid',
                    timestamp: new Date().toISOString(),
                    detail: error.response?.data?.detail
                });
                
                // Limpar todos os tokens usando a função centralizada
                clearAuthData('refresh_token_expired');
                
                // Lançar erro amigável (não será reportado ao Sentry)
                throw new SessionExpiredError('Sessão expirada. Faça login novamente.', 'refresh_token_expired_or_invalid');
            }
            
            // Verificar se é erro de rede usando helper function centralizada
            // Isso detecta: sem resposta, timeout, erros 5xx, e outros erros de rede
            if (isNetworkError(error)) {
                // Type narrowing: verificar se é um erro do Axios com response
                const axiosError = error as any;
                const httpStatus = 'response' in axiosError && axiosError.response?.status;
                const errorCode = 'code' in axiosError ? axiosError.code : undefined;
                
                // Log detalhado do erro de rede (sem reportar ao Sentry - erro temporário esperado)
                logger.error('Erro de rede ao renovar token', error, {
                    hasRequest: 'request' in axiosError && !!axiosError.request,
                    hasResponse: 'response' in axiosError && !!axiosError.response,
                    httpStatus: httpStatus,
                    message: axiosError.message || error.message,
                    code: errorCode,
                    isNetworkError: true,
                    url: environment.apiUrl + '/api/token/refresh/',
                    timestamp: new Date().toISOString(),
                    note: 'Erro de rede temporário - tokens não serão limpos',
                    skipSentry: true // Não reportar ao Sentry - erro temporário esperado
                });
                
                analytics.addBreadcrumb(
                    'Network error during token refresh - preserving tokens (not reporting to Sentry)', 
                    'auth', 
                    'warning'
                );
                
                // Se já é NetworkError, re-lançar (pode ter sido criado em outro lugar)
                // Caso contrário, criar novo NetworkError para distinguir de erros de autenticação
                // Isso permite que o interceptor não limpe tokens em caso de erro de rede temporário
                if (error instanceof NetworkError) {
                    throw error;
                }
                throw new NetworkError('Erro de conexão. Verifique sua internet e tente novamente.', {
                    originalError: error,
                    httpStatus: httpStatus,
                    code: errorCode
                });
            }
            
            // Para outros erros, lançar SessionExpiredError para consistência
            // (não será reportado ao Sentry como erro crítico)
            throw new SessionExpiredError('Sessão expirada. Faça login novamente.', 'unknown_refresh_error');
        } finally {
            // Resetar flags
            isRefreshing = false;
            refreshPromise = null;
        }
    })();

    return refreshPromise;
};

// Função para verificar se o token está próximo do vencimento
export const isTokenExpiringSoon = (
    token: string, 
    minutesBeforeExpiry: number = AUTH_CONSTANTS.TOKEN.DEFAULT_REFRESH_THRESHOLD_MINUTES
): boolean => {
    try {
        const parsedJWT = parseJwt(token);
        if (!parsedJWT || !parsedJWT.exp) return true;
        
        const now = Math.floor(Date.now() / 1000);
        const expiryTime = parsedJWT.exp;
        const timeUntilExpiry = expiryTime - now;
        
        return timeUntilExpiry < (minutesBeforeExpiry * AUTH_CONSTANTS.TOKEN.SECONDS_PER_MINUTE);
    } catch (error) {
        return true;
    }
};

// Função para renovar token automaticamente se necessário
export const refreshTokenIfNeeded = async (): Promise<boolean> => {
    const currentToken = getAuthToken();
    
    if (!currentToken) return false;
    
    if (isTokenExpiringSoon(currentToken)) {
        try {
            const newTokens = await refreshAccessToken();
            
            // Obter o valor de remember_me para manter a persistência correta
            const rememberMe = localStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.REMEMBER_ME) === 'true';
            
            // Atualizar tokens usando as funções centralizadas (mantém o storage correto)
            setAuthToken(newTokens.access, rememberMe);
            // Alguns backends não retornam refresh no endpoint de refresh.
            // Preservar o refresh existente se não houver rotação.
            if (newTokens.refresh) {
                setRefreshToken(newTokens.refresh, rememberMe);
            }
            
            return true;
        } catch (error) {
            // SessionExpiredError não deve ser logado como erro crítico aqui
            // O useTokenRefresh já trata esse erro adequadamente
            if (!isSessionExpiredError(error)) {
                logger.error('Erro ao renovar token', error as Error);
            }
            return false;
        }
    }
    
    return true;
};